import React, { type JSX } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@core-ui/Components/ui/dialog.tsx';
import { Input } from '@core-ui/Components/ui/input.tsx';
import { Button } from '@core-ui/Components/ui/button.tsx';
import { InputSelect, InputSelectTrigger } from '@ui/shared/customSelect.tsx';
import { type SelectOption, type NumbersForm } from '@types';
import { cn } from '@core-ui-lib/utils.ts';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@core-ui/Components/ui/checkbox.tsx';

interface Props {
  open: boolean;
  setOpen: (v: boolean) => void;
  form: NumbersForm;
  setForm: (f: NumbersForm) => void;
  onSave: () => void;
  typeOptions: SelectOption[];
  orgOptions: SelectOption[];
  isEdit: boolean;
  errors?: Partial<Record<
    'number' | 'location' | 'ownerName' | 'active' | 'type',
    string
  >>;
  clearError?: (field: 'number' | 'location' | 'ownerName' | 'active' | 'type') => void;
  isSuperAdmin: boolean;
}

export default function NumberDialog ({
  open, setOpen, form, setForm, onSave, typeOptions, orgOptions, isEdit, errors = {}, clearError, isSuperAdmin
}: Props): React.JSX.Element {
  const { t } = useTranslation();

  const errorText = (field: keyof NonNullable<Props['errors']>): JSX.Element | null =>
    ((errors?.[field]) != null) ? (
      <p data-qa-id={`error-${field}`} className="mt-1 text-xs text-destructive">{errors[field]}</p>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={setOpen} modal={true}>
      <DialogContent
        onOpenAutoFocus={(event) => {
          if (isEdit) {
            event.preventDefault();
          }
        }}
        className={cn(
          'rounded-2xl shadow-2xl w-full max-w-lg',
          'bg-card text-foreground border border-border',
          'data-[state=open]:animate-in data-[state=open]:fade-in-0',
          'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          '[&_div[data-radix-dialog-overlay]]:bg-black/60',
          '[&_div[data-radix-dialog-overlay]]:backdrop-blur-sm'
        )}
      >

        <DialogHeader className="mb-2">
          <DialogTitle className="text-xl font-semibold text-left">
            {isEdit
              ? t('adminConsole.numberDialog.edit', 'Edit Number')
              : t('adminConsole.numberDialog.create', 'Create Number')}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="text-sm font-medium block mb-1 text-muted-foreground">
              {t('adminConsole.numberDialog.number', 'Number')}
            </label>
            <Input
              data-qa-id="number"
              placeholder={t('adminConsole.numberDialog.numberPh', 'Enter number')}
              value={form.number}
              onChange={(e) => {
                setForm({ ...form, number: e.target.value }); clearError?.('number');
              }}
              disabled={!isSuperAdmin}
              aria-invalid={!(errors?.number == null)}
              className={cn(
                'bg-background placeholder:text-muted-foreground',
                'border',
                (errors?.number != null)
                  ? 'border-destructive focus-visible:ring-destructive/40'
                  : 'border-border'
              )}
            />
            {errorText('number')}
          </div>

          {isSuperAdmin &&
            <div>
              <div className="col-span-2">
                <label className="text-sm font-medium block mb-1 text-muted-foreground">
                  {t('adminConsole.numberDialog.location', 'Location')}
                </label>
                <Input
                  data-qa-id="location"
                  placeholder={t('adminConsole.numberDialog.locationPh', 'Minneapolis, US')}
                  value={form.location}
                  onChange={(e) => {
                    setForm({ ...form, location: e.target.value }); clearError?.('location');
                  }}
                  aria-invalid={!(errors.location == null)}
                  className={cn(
                    'bg-background placeholder:text-muted-foreground',
                    'border',
                    (errors?.location != null)
                      ? 'border-destructive focus-visible:ring-destructive/40'
                      : 'border-border'
                  )}
                />
                {errorText('location')}
              </div>

              <div className="col-span-2">
                <label className="text-sm font-medium block mb-1 text-muted-foreground">
                  {t('adminConsole.numberDialog.type', 'numberType')}
                </label>
                <InputSelect
                  options={typeOptions}
                  value={form.type ?? ''}
                  onValueChange={(val) => {
                    const updatedForm = {
                      ...form,
                      type: val,
                    };

                    if (val === 'GLOBAL') {
                      updatedForm.ownerId = null;
                      updatedForm.ownerName = null;
                    }

                    setForm(updatedForm);
                    clearError?.('type');
                  }}


                  placeholder={t('adminConsole.numberDialog.typePh', 'Select type...')}
                  clearable
                >
                  {({
                    options,
                    placeholder,
                    clearable,
                    selectedValue,
                    isPopoverOpen,
                    setIsPopoverOpen,
                    onClearAllOptions,
                  }) => (
                    <InputSelectTrigger
                      options={options}
                      placeholder={placeholder}
                      clearable={clearable}
                      disabled={false}
                      selectedValue={selectedValue}
                      isPopoverOpen={isPopoverOpen}
                      setIsPopoverOpen={setIsPopoverOpen}
                      onClearAllOptions={onClearAllOptions}
                      aria-invalid={!(errors?.type == null)}
                      className={cn(
                        'w-full rounded-md px-3 py-2 text-left',
                        'bg-background hover:bg-muted/50',
                        'border',
                        (errors?.type != null)
                          ? 'border-destructive focus-visible:ring-2 focus-visible:ring-destructive/40'
                          : 'border-border'
                      )}
                    />
                  )}
                </InputSelect>
                {errorText('type')}
              </div>

              {form.type === 'ORGANIZATION' && (
                <div className="col-span-2">
                  <label data-qa-id="organization" className="text-sm font-medium block mb-1 text-muted-foreground">
                    {t('adminConsole.numberDialog.organization', 'Organization')}
                  </label>
                  <InputSelect
                    data-qa-id="organization"
                    options={orgOptions}
                    value={form.ownerId ?? ''}
                    onValueChange={(val: string | null) => {
                      const selectedOrg = orgOptions.find((opt) => opt.value === val);
                      setForm({
                        ...form,
                        ownerId: val,
                        ownerName: selectedOrg?.label ?? null,
                      });
                      clearError?.('ownerName');
                    }}
                    placeholder={t('adminConsole.numberDialog.organizationPh', 'Select organization...')}
                    clearable
                  >

                    {({
                      options,
                      placeholder,
                      clearable,
                      disabled,
                      selectedValue,
                      isPopoverOpen,
                      setIsPopoverOpen,
                      onClearAllOptions,
                    }) => (
                      <InputSelectTrigger
                        options={options}
                        placeholder={placeholder}
                        clearable={clearable}
                        disabled={disabled}
                        selectedValue={selectedValue}
                        isPopoverOpen={isPopoverOpen}
                        setIsPopoverOpen={setIsPopoverOpen}
                        onClearAllOptions={onClearAllOptions}
                        aria-invalid={!(errors?.ownerName == null)}
                        className={cn(
                          'w-full rounded-md px-3 py-2 text-left',
                          'bg-background hover:bg-muted/50',
                          'border',
                          (errors?.ownerName != null)
                            ? 'border-destructive focus-visible:ring-2 focus-visible:ring-destructive/40'
                            : 'border-border'
                        )}
                      />
                    )}
                  </InputSelect>
                  {errorText('ownerName')}
                </div>
              )}
            </div>
          }
        </div>

        <div className="flex justify-center">
          <label className="text-sm font-medium mb-2 ml-4 block text-muted-foreground">
            {t('adminConsole.numberDialog.active', 'Active')}
          </label>
          <Checkbox
            data-qa-id="active-number" className={'flex justify-center ml-4'}
            checked={!(form.active === false)}
            onCheckedChange={(checked) => {
              setForm({ ...form, active: checked === true });
            }}>
          </Checkbox>
        </div>

        <div className="flex justify-end mt-6 gap-4">
          <Button
            data-qa-id={isEdit ? 'cancel-edit-number-button' : 'cancel-create-number-button'}
            variant="outline"
            onClick={() => {
              setOpen(false);
            }}
            className="border border-border text-foreground hover:bg-muted"
          >
            {t('adminConsole.numberDialog.cancel', 'Cancel')}
          </Button>
          <Button
            data-qa-id={isEdit ? 'save-number-form-button' : 'create-number-form-button'}
            onClick={onSave}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {isEdit
              ? t('adminConsole.numberDialog.save', 'Save')
              : t('adminConsole.numberDialog.create', 'Create')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
